import type { Nullable, PutBody } from '@/types'
import type { TmWrappers } from '@/wrappers'
import type { PhoneWithCountryCode } from '@/services/forms/types'
import type { AvatarFile } from '@/data/models/domain/Contact'
import type { TmCountryCode, StringPatch, NumberPatch, ArrayNumbersPatch } from '@/services/types'
import type { AllAvatarColors } from '@/constants/avatar'
import type { IconName } from '@/assets/icons/icons'
import type { CustomFieldValue } from '@/data/models/domain/CustomFieldValue'
import type { CUSTOM_FIELD_FORM_FIELD_NAME, CustomFieldValueType } from '@/services/domain/cusomFields/types'
import type { ValidationRuleError } from '@/services/validation/types'
import type { ValidationRules } from '@/services/validation/validationRulesBuilderService'

export type ContactPostBody = {
  id?: string
  firstName: string
  lastName: string
  country: Nullable<TmCountryCode>
  phone: string
  whatsappChannel?: ContactWhatsappChannelPostBody | null
  companyName: string | null
  lists?: string[]
  email: string
  avatarId: string
  blocked?: boolean
  customFields?: Array<Pick<CustomFieldValue, 'fieldId' | 'fieldType' | 'value'>>
  ownerId?: Nullable<string>
  tagIds?: string[]
}
export type ContactImportResultPostBody = Omit<ContactPostBody, 'lists'>

export type ContactPutBody = PutBody<ContactPostBody>

export type ContactPatchBody = Partial<Omit<ContactPostBody, 'id'>>

export interface RepositoryContactPatchBody {
  firstName?: StringPatch | null
  lastName?: StringPatch | null
  phone?: StringPatch | null
  email?: StringPatch | null
  companyName?: StringPatch | null
  lists?: ArrayNumbersPatch | null
  blocked?: boolean | null
  customFields?: {
    value: Array<Pick<CustomFieldValue, 'fieldId' | 'fieldType' | 'value'>>
  } | null
  country?: StringPatch | null
  avatarId?: NumberPatch | null
  whatsappChannel?: {
    value: ContactWhatsappChannelPostBody | null
  } | null
  ownerId?: NumberPatch | null
  tagIds?: ArrayNumbersPatch | null
  fbMessengerSenderId?: StringPatch | null
  instagramSenderId?: StringPatch | null
}
export type ContactWhatsappChannelPostBody = {
  phone: string
  country: string
}

export const blockedContactWrapper: TmWrappers = 'blockedContact'

export const unsubscribedAfterOptionsAmount = 10

export type ContactImportSettingsFormSchema = {
  lists: string[]
  unsubscribe: boolean
  tagsOn: boolean
  tags: string[]
  block: boolean
  landline: boolean
}

export type ContactFacet = {
  contact: number
  allContact: number
  hiddenList: number
  list: number
  filteredView: number
  customField: number
  tag: number
  import: number
  unsubscriber: number
  unsubscriberEmail: number
  blockedContact: number
}

export type ContactFormSchema = {
  fullName: {
    firstName: string
    lastName: string
  }
  firstName: string
  lastName: string
  avatar: '' | AvatarFile
  phone: PhoneWithCountryCode
  whatsappPhone: PhoneWithCountryCode
  listIds: string[]
  email: string | null
  companyName: string
  ownerId: string
  tagIds: string[]
  fbMessengerSenderId: string
  instagramSenderId: string
  [CUSTOM_FIELD_FORM_FIELD_NAME]: Record<string, CustomFieldValueType | null>
}

// Correct types respecting the exportHandler usage
export type ContactFormExportSchema = Omit<ContactFormSchema, typeof CUSTOM_FIELD_FORM_FIELD_NAME> & {
  [CUSTOM_FIELD_FORM_FIELD_NAME]: Array<Pick<CustomFieldValue, 'fieldId' | 'fieldType' | 'value'>>
}

export interface BeforeChangeEditingIdPreparerBody {
  listIds: string[]
}

export type BeforeChangeEditingIdPreparer = (e: BeforeChangeEditingIdPreparerBody) => BeforeChangeEditingIdPreparerBody

export type AvatarPropsForContact<T extends AllAvatarColors | undefined> =
  | {
      type: 'url'
      url: string
    }
  | {
      type: 'customIcon'
      customIcon: IconName
      placeholderCustomSize: string
      color: T
    }
  | {
      type: 'name'
      name: string | undefined
      placeholderCustomSize: string
      color: T
    }

export type TAvatarProps = {
  name?: string
  initials?: string
  customIcon?: IconName
  color?: AllAvatarColors
  placeholderCustomSize?: string
  url?: string
}

export type ContactImportRouteParams = {
  blocked?: boolean
  unsubscribed?: boolean
  contactListId?: string
  carrierId?: string
}

export const contactNavigationOrder = [
  'contact',
  'list',
  'listFolders',
  'filteredView',
  'tag',
  'note',
  'import',
  'unsubscriber',
  'blockedContact',
] as const

export type ContactNavigationOrderKey = (typeof contactNavigationOrder)[number]

export const FETCH_CONTACTS_COUNTER_ID = 'FETCH_CONTACTS_COUNTER_ID'

export interface UnsubscribeContactPayload {
  phone?: string
  email?: string
  blockIncoming?: boolean
}

export const contactConflictRuleName = 'conflicted'
export interface ContactValidationRuleConflictErrorPayload {
  conflictedId: string
}
export type ContactValidationRuleConflictError = true | ValidationRuleError<ContactValidationRuleConflictErrorPayload>

export interface ContactFormBuildingParams {
  buildPhoneValidators?: (rules: ValidationRules<PhoneWithCountryCode>) => void
  phoneInputHandler?: () => void
  buildEmailValidators?: (rules: ValidationRules<string>) => void
  emailInputHandler?: () => void
}

export type ContactFilterName =
  | 'tags'
  | 'country'
  | 'owner'
  | 'creationDate'
  | 'updatedAt'
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'whatsappPhone'
  | 'email'
  | 'companyName'
  | 'lists'
  | 'createdBy'
  | 'source'
  | 'lastInboundMessageAt'
  | 'lastOutboundMessageAt'
  | 'lastInboundEmailAt'
  | 'lastOutboundEmailAt'

export enum ContactArchivedFilterType {
  /**
   * Active only: not archived, not blocked, not unsubscribed.
   */
  Default = 0,
  Blocked = 3,
  Unsubscribed = 5,
  Archived = 10,
}
